"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePathname, useSearchParams } from "next/navigation";

import type { ReplayManifest, ReplayStateLine } from "@/lib/live/replay/replay-journal";
import type { VideoSubject } from "@/lib/live/video/video-types";
import { listTakes, takeLabels, type ReplayTake } from "@/lib/live/replay/take-lines";
import {
  fetchManifest,
  fetchReplay,
  fetchTakeAnalysis,
  linksExpiring,
  type ReplayApiTake,
  loadJournals,
  replayFileUrl,
  type ReplayDescription,
} from "@/lib/live/replay/replay-loader";
import { ApiError } from "@/lib/api/api-client";
import { recordedCameras } from "@/lib/live/replay/replay-cameras";
import { buildReplaySongs, type ReplayMark } from "@/lib/live/replay/replay-song";
import { readCorrectedSubjects, recordedMeasuresHidden, recordedSubject } from "@/lib/live/replay/replay-subjects";
import { createSongFromRawAnalysis } from "@/lib/music/song-factory";
import {
  correctRecordingCamera,
  deleteRecitalTake,
  getTakeRecital,
  isRecitalId,
  publishRecitalReplay,
  renameRecitalTake,
  type TakeDeletion,
} from "@/lib/api/recitals-api";
import { takeIdFromPathname } from "@/lib/live/recital-routes";
import { announceTake, forgetTake, takeWatchAsked } from "@/lib/live/replay/take-behind";
import { whenAudioRunning } from "@/lib/audio/audio-running";
import { isStudioPathname } from "@/lib/studio/studio-routes";
import { SongControler } from "@/lib/track/controler/song-controler";

import { useStudio } from "./studio-context";

/**
 * Rejouer un récital enregistré, dans le studio qui l'a montré en direct.
 *
 * # Un morceau, pas un flux
 *
 * Un enregistrement se charge **comme un fichier** : les gestes deviennent des
 * notes sur la grille de la séance (`replay-song.ts`), le tout devient un
 * morceau, et le studio le prend par la porte qu'il connaît —
 * `createSongFromRawAnalysis`, `SongControler`, `addTrack`. Le rouleau est
 * plein dès le départ, la minimap entière, et la lecture, la pause, la molette,
 * le glissé, la boucle sont ceux du studio, sans une ligne de plus.
 *
 * La première version rejouait les paquets à l'heure, comme une salle : les
 * notes apparaissaient au fil de la tête de lecture, la minimap restait vide, et
 * revenir en arrière effaçait ce qui avait été dessiné. Un récital enregistré
 * n'est pas un direct : son futur est écrit.
 *
 * # Ce qui reste d'ici
 *
 * Charger, dire où en est le chargement, et tenir de quoi caler les images :
 * les marques d'horloge du morceau et les caméras du manifeste. Le reste
 * appartient au studio.
 */

/** Un fichier d'une caméra : ce qu'elle a filmé d'un trait, à une définition. */
export interface ReplaySegment {
  /** L'adresse du fichier, jeton compris. */
  url: string;
  /** L'instant de séance du temps zéro du fichier. */
  originMs: number;
  durationMs: number;
}

/** Une caméra du récital enregistré, telle que le lecteur la montre. */
export interface ReplayVideo {
  /**
   * L'identité LiveKit de qui la publiait : une caméra par publieur, comme en
   * direct. C'est aussi la clé sous laquelle la séance disait ce qu'elle
   * filmait, et sous laquelle l'interprète le corrige (`subjects`).
   */
  id: string;
  /** Qui filmait. */
  name: string;
  /**
   * L'instant de séance de sa toute première image, éclats écartés compris :
   * sa vignette paraît dès là (`segmentAt`).
   */
  startMs: number;
  /**
   * Ses fichiers, dans l'ordre du temps : un par définition, un par
   * republication — la vignette passe de l'un à l'autre en chemin. Jamais
   * vide. Voir `replay-cameras.ts`.
   */
  segments: readonly ReplaySegment[];
}

export interface ReplayState {
  /** Le récital rejoué, ou `null` quand on ne rejoue rien. */
  recitalId: string | null;
  status: "idle" | "loading" | "ready" | "empty" | "error";
  error: string | null;
  /**
   * Le nom de l'enregistrement, tel que l'API le nomme — son nom propre, sinon
   * le titre du récital. C'est celui que prend une prise qui n'a pas le sien.
   */
  title: string | null;
  /** Les caméras enregistrées, deux au plus, comme en direct. */
  videos: readonly ReplayVideo[];
  /**
   * À quelle heure de la salle chaque tick du morceau a sonné — de quoi placer
   * les images (`wallClockAt`). Plus d'une marque quand le récital s'est
   * interrompu : les ticks s'arrêtent alors, l'horloge non.
   */
  marks: readonly ReplayMark[];
  /**
   * Les prises, quand l'interprète s'y est repris. Une par morceau de la
   * séquence, dans l'ordre ; vide quand l'enregistrement n'a qu'une traversée
   * — il n'y a alors rien à choisir.
   */
  takes: readonly ReplayTake[];
  /** Le rang de la prise ouverte. Zéro quand il n'y a rien d'ouvert. */
  current: number;
  /** L'interprète regarde son propre enregistrement : lui seul publie. */
  mine: boolean;
  /**
   * Quelque chose de cet enregistrement est ouvert au lien du récital : tout,
   * ou une prise. C'est la porte, pas le détail — chaque prise dit d'elle-même
   * si elle est publiée.
   */
  published: boolean;
  /**
   * La prise ouverte porte une vraie analyse — sections, accords, tonalité —
   * et non les seules notes rebâties depuis les journaux. Ce qui n'a de sens
   * que dans ce cas (la structure, le ton) s'y raccroche.
   */
  analyzed: boolean;
  /** Ouvre une autre prise, à son début. */
  goToTake(number: number): void;
  /** Publie cette prise, ou la retire. Interprète seulement. Faux si l'API refuse. */
  publishTake(number: number, published: boolean): Promise<boolean>;
  /**
   * Nomme une prise, ou lui rend son nom d'emprunt (une chaîne vide).
   * Interprète seulement. Faux si l'API refuse. C'est le nom sous lequel elle
   * se partage, et celui que le rouleau écrit à son début.
   */
  renameTake(number: number, title: string): Promise<boolean>;
  /**
   * Publie l'enregistrement entier, ou le retire. Interprète seulement.
   *
   * Le geste des dépôts **sans prises** — une seule traversée, ou un
   * enregistrement d'avant le découpage : il n'y a alors rien à viser, et le
   * lien du récital mène au tout.
   */
  publishAll(published: boolean): Promise<boolean>;
  /**
   * Supprime une prise : la ligne et les octets. Interprète seulement.
   *
   * Rend ce que l'API a laissé — `null` quand elle a refusé, et rien n'a
   * bougé. Un `recording` à `null` dit que c'était la dernière : le dépôt est
   * parti en entier, et il n'y a plus de replay à regarder.
   *
   * Le replay se recharge dans la foulée : la séquence du studio porte un
   * morceau par prise, et en retirer un se fait en rouvrant le dépôt, pas en
   * bricolant la séquence sous la tête de lecture.
   */
  deleteTake(number: number): Promise<TakeDeletion | null>;
  /**
   * Ce que filme chaque caméra, par vidéo : les mains sur le clavier, ou le
   * visage — ce que l'interprète a corrigé après coup, sinon ce que la séance
   * disait, sinon des mains (`recordedSubject`). C'est ce qui place l'image.
   *
   * À côté des vidéos et non posé sur elles : corriger une caméra ne doit rien
   * changer à la liste, dont chaque vignette tient son élément et son calage.
   */
  subjects: ReadonlyMap<string, VideoSubject>;
  /**
   * Dit ce que filmait une caméra, par l'identité de qui la publiait.
   * Interprète seulement. L'image change de place tout de suite, et revient si
   * l'API refuse — faux, alors.
   */
  setCameraSubject(participant: string, subject: VideoSubject): Promise<boolean>;
  /**
   * L'interprète avait masqué les lignes de mesure pendant la prise courante :
   * le replay les masque aussi (`recordedMeasuresHidden`).
   */
  measuresHidden: boolean;
  /**
   * Une caméra montre son image à la position de la tête — ou aucune n'est à
   * montrer là. C'est ce qu'attend la lecture automatique : voir
   * `ReplaySession`.
   */
  reportVideoReady(): void;
  /**
   * Renouvelle le droit de lire les fichiers s'il tombe, ou tombera bientôt :
   * les vidéos repartent alors avec une adresse neuve. Sans effet sinon — à
   * appeler sans compter, par exemple quand une vidéo échoue.
   */
  renewLinks(): void;
}

const IDLE: ReplayState = {
  recitalId: null,
  status: "idle",
  error: null,
  title: null,
  videos: [],
  marks: [],
  takes: [],
  current: 0,
  mine: false,
  published: false,
  analyzed: false,
  goToTake: () => {},
  publishTake: () => Promise.resolve(false),
  renameTake: () => Promise.resolve(false),
  publishAll: () => Promise.resolve(false),
  deleteTake: () => Promise.resolve(null),
  subjects: new Map(),
  setCameraSubject: () => Promise.resolve(false),
  measuresHidden: false,
  reportVideoReady: () => {},
  renewLinks: () => {},
};

const ReplayContext = createContext<ReplayState>(IDLE);

/**
 * Combien de temps on attend qu'une caméra ait son image à la tête avant de
 * regarder sans elle, en millisecondes.
 */
const CAMERA_PATIENCE_MS = 12_000;

/**
 * Combien de temps une lecture demandée par « Regarder » attend que le son se
 * réveille, en millisecondes. Au-delà, le bouton de lecture reste le recours.
 */
const AUDIO_WAKE_MS = 4_000;

// Réexporté d'ici : les écrans du replay prennent tout de ce contexte, et
// aller chercher un type dans `lib/` pour lire une liste qu'il rend serait un
// détour sans raison.
export type { ReplayTake };

/** L'adresse d'un replay dans le studio : `/studio?replay=<récital>`. */
export const REPLAY_PARAM = "replay";

/**
 * La prise à ouvrir : `&take=<n>`, le rang compté pendant la séance, ou
 * `?take=<uuid>` **seul**, l'identifiant de la prise.
 *
 * Le rang est ce que pose la salle d'attente, à côté de `?replay=`. Les liens
 * qui sortent — la page d'une prise partagée, « Mes publications » — ne
 * portent que l'identifiant : ni le récital, ni le rang, qui dirait combien
 * d'essais il a fallu. Le récital se retrouve alors par l'API
 * (`TakeReplay`). Absente ou inconnue, on ouvre la première — tout est là de
 * toute façon, la séquence porte l'enregistrement entier.
 */
export const REPLAY_TAKE_PARAM = "take";

export function useReplay(): ReplayState {
  return useContext(ReplayContext);
}

export function ReplayProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const watching = isStudioPathname(pathname);
  /*
    La page d'une prise prépare son replay derrière elle (2026-09-29) : notes,
    analyse, caméras, pendant qu'on lit son titre. « Regarder » attend qu'il
    soit prêt, et son clic lance la lecture (`take-behind.ts`).

    Même clé que `/studio?take=<id>`, où mène le bouton : la séance montée
    derrière la page est celle qu'on retrouve dans le studio, rien ne se
    recharge au clic.
  */
  const behind = watching ? null : takeIdFromPathname(pathname);

  if (!watching && behind === null) {
    return <ReplayContext.Provider value={IDLE}>{children}</ReplayContext.Provider>;
  }

  const recitalId = watching ? searchParams.get(REPLAY_PARAM) : null;
  const asked = behind ?? searchParams.get(REPLAY_TAKE_PARAM) ?? "";

  // Une prise seule, par son identifiant : le récital est à demander, et c'est
  // la séance qui le demande.
  const byTake = recitalId === null && isRecitalId(asked);

  // Sans récital à rejouer, rien n'est monté : le lecteur n'existe pas plutôt
  // que d'exister éteint.
  if (recitalId === null && !byTake) {
    return <ReplayContext.Provider value={IDLE}>{children}</ReplayContext.Provider>;
  }

  // Remonté d'un récital à l'autre : l'état de départ est « en chargement », et
  // le poser au rendu plutôt que dans un effet évite un premier rendu inutile.

  return (
    <ReplaySession
      key={recitalId ?? asked}
      recitalId={recitalId}
      take={/^\d+$/.test(asked) ? Number(asked) : asked || 1}
      watching={watching}
    >
      {children}
    </ReplaySession>
  );
}

function ReplaySession({
  recitalId: asked,
  take,
  watching,
  children,
}: {
  /** `null` : ouverte par la prise seule (`?take=<uuid>`), le récital est à demander. */
  recitalId: string | null;
  /** Le rang, ou l'identifiant de la prise — voir `REPLAY_TAKE_PARAM`. */
  take: number | string;
  /** Dans le studio, et non derrière la page de la prise : la lecture peut partir. */
  watching: boolean;
  children: ReactNode;
}) {
  const { studio } = useStudio();

  /*
    La prise à ouvrir, et le tour de chargement.

    L'adresse pose la première (`&take=2`) ; supprimer une prise change les
    deux — on rouvre le dépôt sur une survivante. Un état plutôt que la
    propriété : la séquence du studio porte un morceau par prise, et en retirer
    un se fait en rechargeant, pas en bricolant la séquence sous la tête de
    lecture.
  */
  const [opened, setOpened] = useState<number | string>(take);
  const [reload, setReload] = useState(0);

  const [status, setStatus] = useState<ReplayState["status"]>("loading");
  const [error, setError] = useState<string | null>(null);

  /*
    Le récital, quand l'adresse ne portait que la prise (`?take=<uuid>`) : ce
    que les liens partagés écrivent, pour ne dire ni le récital ni le rang.

    Demandé **ici**, dans la séance déjà montée, et non par un composant qui
    la monterait une fois la réponse venue : changer la forme de l'arbre au
    milieu remontait tout le studio — et perdait l'entrée qu'on venait d'y
    faire. Le temps de la question, la séance est en chargement sous un
    récital vide, jamais lu tel quel : rien n'appelle l'API avant « prêt ».
  */
  const [resolved, setResolved] = useState(asked);
  const recitalId = resolved ?? "";

  useEffect(() => {
    if (asked !== null || typeof take !== "string") {
      return;
    }

    const abort = new AbortController();

    getTakeRecital(take, abort.signal).then(setResolved, (cause: unknown) => {
      if (abort.signal.aborted) {
        return;
      }

      const unknown = cause instanceof ApiError && cause.status === 404;

      setError(unknown ? "Cette prise n'est pas publiée." : "Le replay ne s'ouvre pas.");
      setStatus("error");
    });

    return () => abort.abort();
  }, [asked, take]);
  const [description, setDescription] = useState<ReplayDescription | null>(null);
  const [manifest, setManifest] = useState<ReplayManifest | null>(null);
  /** Les états de séance que l'enregistreur a gardés : ce que la séance disait de chaque caméra. */
  const [states, setStates] = useState<readonly ReplayStateLine[]>([]);
  /** Ce que l'interprète a corrigé, tel qu'on le montre — la dernière réponse de l'API, ou le geste en cours. */
  const [corrected, setCorrected] = useState<Readonly<Record<string, VideoSubject>>>({});
  /** La dernière table que l'API a retenue : ce qu'on remet quand elle refuse un geste. */
  const confirmed = useRef<Readonly<Record<string, VideoSubject>>>({});
  /** La fiche du moment, lue par ce qui doit rester stable : voir `renewLinks`. */
  const described = useRef<ReplayDescription | null>(null);
  /** Le renouvellement en cours : deux vidéos qui échouent ensemble n'en font qu'un. */
  const renewing = useRef<Promise<void> | null>(null);
  const [marks, setMarks] = useState<readonly ReplayMark[]>([]);
  const [takes, setTakes] = useState<readonly ReplayTake[]>([]);
  const [current, setCurrent] = useState(0);
  /** Quelle prise porte quel contrôleur : ce que la séquence ne sait pas dire. */
  const numberOf = useRef(new Map<SongControler, number>());
  /**
   * De quoi réécrire les libellés du rouleau quand une prise change de nom :
   * les prises jouées, dans l'ordre de la séquence, ce que l'API sait de
   * chacune, le nom de l'enregistrement, et qui regarde (`takeLabels`).
   */
  const labelling = useRef<{
    played: readonly { number: number }[];
    known: readonly ReplayApiTake[];
    recordingTitle: string;
    mine: boolean;
  } | null>(null);
  /**
   * Et l'inverse : à quel rang de la séquence chaque prise est posée.
   *
   * Pas l'index dans `takes` : la liste porte aussi les faux départs, qui n'ont
   * pas de morceau. Les confondre ouvrait la prise d'à côté.
   */
  const sequenceOf = useRef(new Map<number, number>());
  /**
   * Les prises qui portent une analyse de l'analyseur, par rang.
   *
   * Un état et non une ref : c'est lu au rendu — la barre latérale et le ton
   * s'y raccrochent —, et une ref ne redéclencherait rien en changeant.
   */
  const [analyzedTakes, setAnalyzedTakes] = useState<ReadonlySet<number>>(new Set());
  /**
   * Les marques d'horloge de chaque prise, par rang.
   *
   * Une caméra se cale sur la prise qu'on regarde, pas sur l'enregistrement :
   * chaque morceau a ses ticks depuis zéro, donc ses marques à lui.
   */
  const marksByTake = useRef(new Map<number, readonly ReplayMark[]>());
  /** Une caméra a son image à la tête (`reportVideoReady`). */
  const [videoReady, setVideoReady] = useState(false);
  /** La lecture est partie seule, ou on y a renoncé : une fois par replay ouvert. */
  const autoplayed = useRef(false);

  useEffect(() => {
    if (studio === null || recitalId === "") {
      return;
    }

    let disposed = false;
    const abort = new AbortController();
    let loaded: SongControler[] = [];
    // Le studio joue son jingle en arrivant : c'est un morceau comme un autre,
    // sur le même transport. Poser le récital pendant qu'il sonne le ferait
    // sonner avec lui, et les deux se disputeraient la tête de lecture.
    let release = () => {};

    const entered = new Promise<void>((resolve) => {
      release = studio.onReady(resolve);
    });

    const start = async () => {
      const found = await fetchReplay(recitalId, abort.signal);
      const read = await fetchManifest(found, abort.signal);
      const journals = await loadJournals(found, read, abort.signal);

      if (disposed) {
        return;
      }

      /*
        Un morceau par prise, chacun repartant du tick zéro : c'est ce qui
        permet de comparer deux essais au même endroit de l'écran.

        Le découpage vient du manifeste, **filtré par la fiche**. Le manifeste
        est la trace brute du dépôt : il porte toutes les prises, publiées ou
        non. La fiche, elle, ne liste à qui n'est pas l'interprète que les
        prises publiées. Bâtir sur le manifeste seul donnait à qui ouvrait le
        lien d'une prise partagée l'accès à toutes les autres — constaté le
        2026-09-22, le jour même où le lien d'une prise a existé.
      */
      // Le nom de l'enregistrement, que prend une prise sans nom propre.
      const recordingTitle = found.title ?? found.recital.title;
      const songs = buildReplaySongs(
        journals.packets,
        journals.states,
        recordingTitle,
        openTakes(read.takes, found),
      );
      const played = songs.filter((song) => song.notes > 0);

      // Tout ce qui suit se voit ou s'entend — les caméras autant que les
      // notes. Le chargement, lui, a eu lieu pendant le jingle : l'attente ne
      // coûte que le temps qu'il reste à jouer.
      await entered;

      if (disposed) {
        return;
      }

      const corrections = readCorrectedSubjects(found.cameras);

      setDescription(found);
      setManifest(read);
      setStates(journals.states);
      confirmed.current = corrections;
      setCorrected(corrections);

      if (played.length === 0) {
        // Un enregistrement sans un geste : il n'y a pas de morceau à charger,
        // et un rouleau vide sans un mot laisserait croire à une panne.
        setStatus("empty");

        return;
      }

      /*
        L'analyse de chaque prise, quand l'API en a une.

        Une prise est une pièce : son MIDI a été écrit au dépôt, et l'API le
        fait analyser à la demande. Avec elle viennent les sections, les
        accords, la tonalité — tout ce que le studio sait montrer d'un morceau
        et qu'un journal de gestes ne porte pas. Sans elle (un dépôt d'avant,
        un analyseur muet), on garde les notes rebâties depuis les journaux :
        on perd la structure, pas la musique.

        Toutes en parallèle, et on attend : la première ouverture d'un récital
        coûte l'analyse, les suivantes la relisent. Bâtir d'abord puis échanger
        ferait sauter le rouleau sous les yeux de qui regarde déjà.
      */
      const analyses = await Promise.all(
        played.map((song) =>
          found.takes?.some((known) => known.number === song.number && known.analyzable === true) === true
            ? fetchTakeAnalysis(recitalId, song.number, abort.signal)
            : Promise.resolve(null),
        ),
      );

      if (disposed) {
        return;
      }

      // La porte qu'emprunte n'importe quel morceau : celle du jingle, d'un
      // exercice engendré, d'un fichier importé.
      const controlers = played.map(
        (song, index) =>
          new SongControler(
            createSongFromRawAnalysis(analyses[index] ?? song.analysis),
            studio.getTransport(),
            studio.getAudioContext(),
          ),
      );

      await Promise.all(controlers.map((controler) => controler.load()));

      if (disposed) {
        for (const controler of controlers) {
          controler.destroy();
        }

        return;
      }

      loaded = controlers;

      const labels = takeLabels(played, found.takes, recordingTitle, found.mine);

      labelling.current = { played, known: found.takes ?? [], recordingTitle, mine: found.mine };
      studio.getSequence().set(controlers.map((controler, index) => ({ controler, label: labels[index] })));

      const wanted = typeof opened === "number" ? opened : found.takes?.find((one) => one.id === opened)?.number;
      const at = Math.max(0, played.findIndex((song) => song.number === wanted));

      studio.getSequence().goTo(at);
      // La tête au début de ce qu'on ouvre, à l'arrêt : c'est l'état d'un
      // morceau qu'on vient d'ouvrir, et le geste de lecture appartient à qui
      // regarde.
      studio.getLocalTransport().detach();

      setAnalyzedTakes(
        new Set(played.filter((song, index) => analyses[index] !== null).map((song) => song.number)),
      );
      marksByTake.current = new Map(played.map((song) => [song.number, song.marks]));
      numberOf.current = new Map(controlers.map((controler, index) => [controler, played[index].number]));
      sequenceOf.current = new Map(played.map((song, index) => [song.number, index]));
      setMarks(played[at].marks);
      setCurrent(played[at].number);
      setTakes(listTakes(found.takes, played));
      setStatus("ready");
    };

    void start().catch((cause: unknown) => {
      if (disposed) {
        return;
      }

      setStatus("error");
      setError(
        cause instanceof Error && cause.message !== ""
          ? cause.message
          : "Ce replay n'a pas pu être ouvert.",
      );
    });

    return () => {
      disposed = true;
      abort.abort();
      release();

      if (loaded.length === 0) {
        return;
      }

      // Comme on referme un morceau : la séquence se vide — en scène comme
      // hors scène —, le transport s'arrête au début, et la grille du récital
      // ne survit pas au départ.
      studio.getSequence().clear();
      studio.stopAllScheduledNotes();
      studio.getLocalTransport().detach();
      studio.getLocalTransport().seekToTick(0);
      studio.resetTimeline();
    };
  }, [opened, recitalId, reload, studio]);

  /*
    La prise courante suit la séquence, pas le sélecteur.

    On change de prise de deux façons : en la choisissant, ou en **jouant
    jusqu'au bout** de la précédente — la séquence passe alors au morceau
    suivant toute seule. Ne réagir qu'au geste laissait la caméra sur les
    marques de la première prise pour tout le reste de l'enregistrement : elle
    montrait le bon film, au mauvais endroit, et plus rien ne collait.
    Constaté le 2026-09-18 sur un récital de trois prises.
  */
  useEffect(() => {
    if (studio === null) {
      return;
    }

    return studio.getSequence().current.subscribe((controler) => {
      const number = controler === null ? undefined : numberOf.current.get(controler);

      if (number === undefined) {
        return;
      }

      setCurrent(number);
      setMarks(marksByTake.current.get(number) ?? []);
    });
  }, [studio]);

  /*
    Changer de prise, c'est la reprendre au début.

    Une prise est un essai entier : on l'écoute depuis la première note, pas
    depuis l'endroit où l'on avait laissé la précédente. La caméra suit les
    marques de la prise ouverte, qui ne sont pas celles de la précédente.
  */
  const goToTake = useCallback(
    (number: number) => {
      const index = sequenceOf.current.get(number);

      if (studio === null || index === undefined) {
        return;
      }

      // Les marques et le rang suivent par l'abonnement ci-dessus : un seul
      // chemin, qu'on ait choisi la prise ou qu'on y soit arrivé en jouant.
      studio.getSequence().goTo(index);
    },
    [studio],
  );

  const publishTake = useCallback(
    async (number: number, published: boolean) => {
      try {
        await publishRecitalReplay(recitalId, published, number);

        setTakes((before) =>
          before.map((entry) => (entry.number === number ? { ...entry, published } : entry)),
        );

        return true;
      } catch (cause) {
        console.warn("Publication d'une prise :", cause);

        return false;
      }
    },
    [recitalId],
  );

  /*
    Nommer une prise, depuis le studio où on l'écoute.

    La liste rendue par l'API est reposée pour ce qui est de cette prise, et le
    rouleau réécrit son étiquette en place : `relabel`, pas `set`, qui ferait
    repartir la séquence du premier morceau sous la tête de qui écoute.
  */
  const renameTake = useCallback(
    async (number: number, title: string) => {
      try {
        const fresh = await renameRecitalTake(recitalId, number, title);
        const own = fresh.find((take) => take.number === number)?.title ?? null;

        setTakes((before) => before.map((entry) => (entry.number === number ? { ...entry, title: own } : entry)));

        const context = labelling.current;

        if (studio !== null && context !== null) {
          const known = context.known.map((take) => (take.number === number ? { ...take, title: own } : take));
          const labels = takeLabels(context.played, known, context.recordingTitle, context.mine);

          labelling.current = { ...context, known };

          for (const [controler, played] of numberOf.current) {
            const at = context.played.findIndex((song) => song.number === played);

            if (at >= 0) {
              studio.getSequence().relabel(controler, labels[at]);
            }
          }
        }

        return true;
      } catch (cause) {
        console.warn("Nom d'une prise :", cause);

        return false;
      }
    },
    [recitalId, studio],
  );

  /*
    Publier l'enregistrement entier.

    Le geste des dépôts sans prises : il n'y a rien à viser, et c'est le lien du
    récital qui s'ouvre. Rien n'est reposé dans `takes` — il est vide —, mais la
    fiche est relue pour que le reste de l'écran (`mine`, `published`) dise ce
    qui est vrai.
  */
  const publishAll = useCallback(
    async (published: boolean) => {
      try {
        await publishRecitalReplay(recitalId, published);
        setDescription((before) => (before === null ? before : { ...before, published }));

        return true;
      } catch (cause) {
        console.warn("Publication d'un enregistrement :", cause);

        return false;
      }
    },
    [recitalId],
  );

  /*
    Supprimer une prise.

    L'API répond avec ce qu'elle a gardé, et on repose cette liste-là plutôt
    que de retirer la ligne de son côté : les octets partent avant la ligne, et
    un refus doit laisser la prise à l'écran pour qu'on puisse réessayer.

    Puis on **recharge**. La séquence du studio porte un morceau par prise ; en
    retirer un sous la tête de lecture demanderait de recalculer les marques,
    les analyses et le rang de chacune — le rechargement fait tout ça par le
    chemin déjà écrit, et la prise ouverte devient une survivante.
  */
  const deleteTake = useCallback(
    async (number: number) => {
      try {
        const left = await deleteRecitalTake(recitalId, number);

        if (left.recording === null) {
          // Plus rien à regarder : l'appelant quitte le studio. Recharger
          // n'ouvrirait qu'une erreur.
          setTakes([]);

          return left;
        }

        setOpened((current) => (current === number ? (left.takes[0]?.number ?? 1) : current));
        setReload((turn) => turn + 1);

        return left;
      } catch (cause) {
        console.warn("Suppression d'une prise :", cause);

        return null;
      }
    },
    [recitalId],
  );

  /*
    Dire ce que filmait une caméra.

    L'image change de place tout de suite : c'est elle que l'interprète
    regarde en le disant, et attendre l'aller-retour ferait croire que le geste
    n'a pas pris. Si l'API refuse, on remet ce qu'elle avait retenu.
  */
  const setCameraSubject = useCallback(
    async (participant: string, subject: VideoSubject) => {
      if (description === null || !description.mine) {
        return false;
      }

      setCorrected((before) => ({ ...before, [participant]: subject }));

      try {
        const kept = await correctRecordingCamera(description.id, participant, subject);

        confirmed.current = kept;
        setCorrected(kept);

        return true;
      } catch (cause) {
        console.warn("Correction d'une caméra :", cause);
        setCorrected(confirmed.current);

        return false;
      }
    },
    [description],
  );

  /*
    Renouveler le droit de lire les fichiers.

    Les vidéos portent un jeton dans leur adresse — un `<video>` n'a pas
    d'en-tête —, et l'adresse présignée du seau vers laquelle l'API les
    redirige vaut ce qui restait à ce jeton. Quelqu'un qui laisse la page
    ouverte la journée et revient le soir trouverait les deux échus : la
    première tranche demandée serait refusée, et la vignette dirait « Image
    indisponible » jusqu'à ce qu'il recharge.

    On redemande donc la fiche — un jeton neuf — quand il tombe ou va tomber :
    au retour sur l'onglet, avant même le premier geste, et quand une vidéo
    échoue. Les vidéos repartent avec leur nouvelle adresse, et le calage les
    replace où en est la tête de lecture.

    Un refus veut dire que le droit lui-même est parti : un replay non publié,
    et une session de compte échue avec la journée. Il n'y a rien à réparer
    d'ici ; on le dit.
  */
  useEffect(() => {
    described.current = description;
  }, [description]);

  const renewLinks = useCallback(() => {
    const current = described.current;

    if (current === null || renewing.current !== null || !linksExpiring(current, Date.now())) {
      return;
    }

    renewing.current = (async () => {
      try {
        const fresh = await fetchReplay(recitalId);
        const corrections = readCorrectedSubjects(fresh.cameras);

        setDescription(fresh);
        confirmed.current = corrections;
        setCorrected(corrections);
      } catch (cause) {
        if (cause instanceof ApiError && (cause.status === 401 || cause.status === 403)) {
          setStatus("error");
          setError("Le droit de revoir ce replay a expiré : reconnecte-toi, puis rouvre-le.");
        } else {
          console.warn("Renouvellement des liens du replay :", cause);
        }
      } finally {
        renewing.current = null;
      }
    })();
  }, [recitalId]);

  useEffect(() => {
    const look = () => {
      if (document.visibilityState === "visible") {
        renewLinks();
      }
    };

    document.addEventListener("visibilitychange", look);

    return () => document.removeEventListener("visibilitychange", look);
  }, [renewLinks]);

  const videos = useMemo(
    () => (description === null || manifest === null ? [] : videosOf(description, manifest)),
    [description, manifest],
  );

  const subjects = useMemo(
    () =>
      new Map(
        videos.map((video) => [video.id, recordedSubject({ participant: video.id, endMs: endOf(video) }, states, corrected)]),
      ),
    [videos, states, corrected],
  );

  const reportVideoReady = useCallback(() => setVideoReady(true), []);

  /*
    Les caméras ont un temps pour paraître, pas davantage.

    Ce qu'on attend d'elles — une image à la tête — peut ne jamais venir : un
    fichier qui échoue, un navigateur qui ne charge rien. Sans limite, le
    bouton « Regarder » de la page d'une prise tournerait pour toujours, et la
    lecture ne partirait jamais. Passé ce délai, on regarde sans elles.
  */
  const [camerasLate, setCamerasLate] = useState(false);

  useEffect(() => {
    if (status !== "ready" || videoReady) {
      return;
    }

    const timer = setTimeout(() => setCamerasLate(true), CAMERA_PATIENCE_MS);

    return () => clearTimeout(timer);
  }, [status, videoReady]);

  /*
    Prêt à regarder : les notes sont posées, et une caméra a son image à la tête
    — ou il n'y en a aucune à attendre. Un replay qui a échoué ou qui est vide
    l'est aussi : le studio le dira (`ReplayNotice`), il n'y a rien à attendre de
    plus.
  */
  const watchable =
    status !== "loading" && (status !== "ready" || videoReady || videos.length === 0 || camerasLate);

  // La page de la prise attend ce moment pour rendre son bouton.
  const takeId = typeof take === "string" ? take : null;

  useEffect(() => {
    if (takeId !== null) {
      announceTake(takeId, watchable);
    }
  }, [takeId, watchable]);

  useEffect(() => {
    if (takeId === null) {
      return;
    }

    return () => forgetTake(takeId);
  }, [takeId]);

  /** La séance est encore montée : une lecture attendue ne part pas dans le vide. */
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;

    return () => {
      alive.current = false;
    };
  }, []);

  /*
    La lecture part seule, décompte compris (2026-09-27).

    Ouvrir le replay, c'était déjà demander à le voir. Mais le décompte attend
    qu'une caméra ait son image : des mains qui jouent sur une vignette noire,
    c'est ce qu'on vient voir qui manque. Sans caméra — ou sans caméra qui filme
    au début de la prise —, rien à attendre.

    Jamais derrière la page d'une prise : on n'y a encore rien demandé. Le clic
    sur « Regarder » y est la demande de lecture (2026-09-29), et c'est aussi
    le geste qui réveille le son — un réveil qui peut ne pas être fini quand on
    arrive dans le studio. Cette lecture-là attend donc que l'audio tourne.

    Une fois par replay ouvert : ni changer de prise ni la recharger après une
    suppression ne relance quoi que ce soit. Et jamais par-dessus un geste :
    qui a déjà lancé la lecture ou le décompte l'a fait lui-même. Sans demande,
    un contexte audio encore suspendu — une adresse collée, un rechargement —
    n'a pas eu de geste : le lancer donnerait un décompte muet, la lecture
    attend donc le bouton.
  */
  useEffect(() => {
    if (autoplayed.current || !watching || studio === null || status !== "ready" || !watchable) {
      return;
    }

    autoplayed.current = true;

    const start = () => {
      if (!alive.current || studio.countIn.active || !studio.getLocalTransport().isDetached) {
        return;
      }

      void studio.startWithCountIn(undefined, { skipSectionTips: true });
    };

    const asked = takeId !== null && takeWatchAsked(takeId);
    const context = studio.getAudioContext();

    if (context.state === "running") {
      start();

      return;
    }

    if (!asked) {
      return;
    }

    // Le tap sur « Regarder » a pu ne rien réveiller — sous Chrome, le son
    // n'obéit au doigt qu'à son lever. Mais la page a reçu un geste, et Chrome
    // accepte alors un réveil même hors de lui : on le demande ici.
    void context.resume().catch(() => {
      // Refusé : l'attente qui suit tombera, et le bouton de lecture restera.
    });

    void whenAudioRunning(context, AUDIO_WAKE_MS).then((running) => {
      if (running) {
        start();
      }
    });
  }, [status, studio, takeId, watchable, watching]);

  const measuresHidden = useMemo(() => {
    const take = manifest?.takes?.find((found) => found.number === current);
    const end = take?.endedAtMs ?? manifest?.endedAtMs ?? Number.POSITIVE_INFINITY;

    return recordedMeasuresHidden(states, end);
  }, [manifest, current, states]);

  const value = useMemo<ReplayState>(
    () => ({
      recitalId,
      status,
      error,
      title: description === null ? null : (description.title ?? description.recital.title),
      videos,
      marks,
      takes,
      current,
      mine: description?.mine ?? false,
      published: description?.published ?? false,
      analyzed: analyzedTakes.has(current),
      goToTake,
      publishTake,
      renameTake,
      publishAll,
      deleteTake,
      subjects,
      setCameraSubject,
      measuresHidden,
      reportVideoReady,
      renewLinks,
    }),
    [
      analyzedTakes,
      current,
      deleteTake,
      description,
      error,
      goToTake,
      marks,
      measuresHidden,
      publishAll,
      publishTake,
      recitalId,
      renameTake,
      renewLinks,
      reportVideoReady,
      setCameraSubject,
      status,
      subjects,
      takes,
      videos,
    ],
  );

  return <ReplayContext.Provider value={value}>{children}</ReplayContext.Provider>;
}

/**
 * Les prises du manifeste que celui qui regarde a le droit d'ouvrir.
 *
 * # Deux listes, et la plus étroite gagne
 *
 * Le **manifeste** décrit le dépôt : toutes les prises, publiées ou non, parce
 * que c'est la trace de ce que l'enregistreur a vu. La **fiche** décrit ce
 * qu'on a le droit de voir : l'interprète y trouve tout, un visiteur les seules
 * prises publiées (`ReplayTakes`, côté API). Le découpage du replay se fait
 * donc sur l'intersection, et pas sur le manifeste seul.
 *
 * L'interprète garde le manifeste entier : la fiche lui rend tout de toute
 * façon, et se rabattre dessus perdrait une prise que l'API n'aurait pas encore
 * annoncée.
 *
 * Une fiche sans prises veut dire « il n'y a rien à découper » — une seule
 * traversée, ou un dépôt d'avant le découpage : on rend alors le manifeste tel
 * quel, et `buildReplaySongs` en fait un seul morceau. Ce cas ne peut pas
 * fuiter : sans prise publiée, il n'y a pas de replay ouvert du tout.
 */
function openTakes(
  manifest: ReplayManifest["takes"],
  description: ReplayDescription,
): NonNullable<ReplayManifest["takes"]> {
  const all = manifest ?? [];

  if (description.mine || description.takes === undefined || description.takes.length === 0) {
    return all;
  }

  const allowed = new Set(description.takes.map((take) => take.number));

  return all.filter((take) => allowed.has(take.number));
}

/**
 * Les caméras d'un enregistrement, prêtes à montrer.
 *
 * Des caméras et non des fichiers : une caméra en laisse plusieurs — un par
 * définition, un par republication —, et les deux premiers fichiers du
 * manifeste ont pu être les deux premiers de la même (`replay-cameras.ts`).
 *
 * Deux au plus, comme en direct (`recital-video.ts`) : au-delà, les vignettes
 * mangent le rouleau qu'on est venu regarder.
 *
 * Le nom de la piste n'est pas lu : il a dit `hands` ou `face` un temps, et
 * ce que filmait une caméra se lit désormais dans la séance (`subjects`).
 */
function videosOf(description: ReplayDescription, manifest: ReplayManifest): ReplayVideo[] {
  const names = new Map(manifest.participants.map((participant) => [participant.identity, participant.name]));

  return recordedCameras(manifest.tracks)
    .slice(0, 2)
    .map((camera) => ({
      id: camera.participant,
      name: names.get(camera.participant) ?? "Caméra",
      startMs: camera.startMs,
      segments: camera.tracks.map((track) => ({
        url: replayFileUrl(description, track.file),
        originMs: track.originMs,
        durationMs: track.durationMs,
      })),
    }));
}

/** L'instant de séance de la dernière image d'une caméra. */
function endOf(video: ReplayVideo): number {
  const last = video.segments[video.segments.length - 1];

  return last === undefined ? Number.NEGATIVE_INFINITY : last.originMs + last.durationMs;
}
